import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Search,
  ChevronDown,
  UserPlus,
  Shuffle,
  Network,
  Download,
  X,
  AlignVerticalJustifyCenter,
  AlignHorizontalJustifyCenter,
} from "lucide-react";
import { Tabs } from "../../components/em/ui";
import { API_URL } from "../../api/api";
import AssignManagerModal from "./AssignManagerModal";
import MassTransferModal from "./MassTransferModal";
import OrgCanvas from "./OrgCanvas";
import useOrgTree from "./useOrgTree";
import "./orgchart.css";

import { toast } from "../../utils/toast";
const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
};

const exportItemStyle = {
  display: "block",
  width: "100%",
  textAlign: "left",
  padding: "8px 10px",
  border: "none",
  background: "transparent",
  borderRadius: 8,
  fontSize: 13,
  cursor: "pointer",
  color: "var(--em-fg1, #111827)",
};

// Filter the tree to only branches that contain a node matching `pred`.
// Keeps ancestors that lead to a match so the user has context.
const pruneTree = (nodes, pred) =>
  nodes
    .map((n) => {
      const kids = pruneTree(n.children || [], pred);
      const self = pred(n);
      if (self || kids.length) return { ...n, children: kids };
      return null;
    })
    .filter(Boolean);

// Walk roots collecting the path of IDs that lead to `targetId`.
const findPath = (roots, targetId) => {
  const path = [];
  const dfs = (node) => {
    path.push(node.id);
    if (node.id === targetId) return true;
    for (const c of node.children || []) {
      if (dfs(c)) return true;
    }
    path.pop();
    return false;
  };
  for (const r of roots) {
    if (dfs(r)) return path;
  }
  return [];
};

export default function OrganisationChart({ readOnly = false }) {
  // Members route in with `readOnly`, hiding write actions even though the
  // backend also enforces admin/HR-only on /assign-manager + /mass-transfer.
  const role = String(localStorage.getItem("role") || "").toLowerCase();
  const isReadOnly = readOnly || role === "member";
  const [activeTab, setActiveTab] = useState("Vertical");
  const [openAssign, setOpenAssign] = useState(false);
  const [openMass, setOpenMass] = useState(false);
  const [zoomPercent, setZoomPercent] = useState(100);
  const [search, setSearch] = useState("");
  const [dept, setDept] = useState("");
  const [highlightId, setHighlightId] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  // Drag-to-reorg confirmation: { empId, targetId } while the modal is open.
  const [reorg, setReorg] = useState(null);
  const [reorgBusy, setReorgBusy] = useState(false);
  const [reorgErr, setReorgErr] = useState(null);

  const canvasRef = useRef(null);

  const tree = useOrgTree();

  // Size the chart card so it fills from its top down to 16px above the
  // viewport bottom — measured (not a hardcoded offset) so it stays correct
  // regardless of header height or wrapping. Re-measured once the tree
  // finishes loading, since the header subtitle text changes then.
  const chartAreaRef = useRef(null);
  const [chartHeight, setChartHeight] = useState(null);
  useEffect(() => {
    const measure = () => {
      if (!chartAreaRef.current) return;
      const top = chartAreaRef.current.getBoundingClientRect().top;
      setChartHeight(Math.max(420, Math.round(window.innerHeight - top - 16)));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [tree.loading]);

  const getActiveRef = () => canvasRef;

  useEffect(() => {
    setZoomPercent(100);
    getActiveRef()?.current?.resetZoom?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Apply department filter + search to the tree.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matches = (n) => {
      if (dept && n.department !== dept) return false;
      if (!q) return true;
      const hay =
        `${n.name} ${n.designation || ""} ${n.email || ""}`.toLowerCase();
      return hay.includes(q);
    };
    if (!q && !dept) return { roots: tree.roots, orphans: tree.orphans };
    return {
      roots: pruneTree(tree.roots || [], matches),
      orphans: (tree.orphans || []).filter((o) => matches(o)),
    };
  }, [tree.roots, tree.orphans, search, dept]);

  // Single-match highlight: when search narrows to one match, highlight it.
  useEffect(() => {
    const q = search.trim().toLowerCase();
    if (!q) {
      setHighlightId(null);
      return;
    }
    const hits = (tree.allEmployees || []).filter((e) => {
      if (dept && e.department !== dept) return false;
      const hay =
        `${e.name} ${e.designation || ""} ${e.email || ""}`.toLowerCase();
      return hay.includes(q);
    });
    setHighlightId(hits.length === 1 ? hits[0].id : null);
  }, [search, dept, tree.allEmployees]);

  const allEmployeesForNetwork = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q && !dept) return tree.allEmployees || [];
    if (q) {
      // For network view, when searching, narrow to matching employees plus
      // their primary-chain ancestors so context is preserved.
      const path = highlightId ? findPath(tree.roots || [], highlightId) : [];
      const keep = new Set(path);
      (tree.allEmployees || []).forEach((e) => {
        const hay =
          `${e.name} ${e.designation || ""} ${e.email || ""}`.toLowerCase();
        if ((!dept || e.department === dept) && hay.includes(q)) {
          keep.add(e.id);
        }
      });
      return (tree.allEmployees || []).filter((e) => keep.has(e.id));
    }
    return (tree.allEmployees || []).filter((e) => e.department === dept);
  }, [tree.allEmployees, tree.roots, search, dept, highlightId]);

  const handlePick = (node) => setHighlightId(node.id);

  // Lookup for names in the reorg confirmation.
  const nodeById = useMemo(() => {
    const m = new Map();
    (tree.allEmployees || []).forEach((e) => m.set(e.id, e));
    return m;
  }, [tree.allEmployees]);

  // Reporting-line path to the highlighted node — used to light up the
  // connectors (and to expand collapsed ancestors when focusing).
  const pathIds = useMemo(
    () => (highlightId ? findPath(tree.roots || [], highlightId) : []),
    [tree.roots, highlightId],
  );

  // Pan/zoom the canvas to centre the highlighted node (search single-match
  // or a click-pick). Runs a beat after render so the tree geometry exists.
  useEffect(() => {
    if (!highlightId) return;
    const t = setTimeout(() => {
      canvasRef.current?.focusNode?.(highlightId, pathIds);
    }, 90);
    return () => clearTimeout(t);
  }, [highlightId, pathIds, activeTab]);

  // Export the whole chart as PNG or PDF.
  const handleExport = async (fmt) => {
    setExportOpen(false);
    if (exporting) return;
    setExporting(true);
    try {
      const dataUrl = await canvasRef.current?.exportImage?.();
      if (!dataUrl) throw new Error("Nothing to export");
      const date = new Date().toISOString().slice(0, 10);
      if (fmt === "png") {
        const a = document.createElement("a");
        a.href = dataUrl;
        a.download = `org-chart-${date}.png`;
        a.click();
      } else {
        const { jsPDF } = await import("jspdf");
        const img = new Image();
        await new Promise((res, rej) => {
          img.onload = res;
          img.onerror = () => rej(new Error("image decode failed"));
          img.src = dataUrl;
        });
        const w = img.width || 1;
        const h = img.height || 1;
        const pdf = new jsPDF({
          orientation: w >= h ? "landscape" : "portrait",
          unit: "pt",
          format: "a4",
        });
        const pw = pdf.internal.pageSize.getWidth();
        const ph = pdf.internal.pageSize.getHeight();
        const margin = 24;
        const scale = Math.min((pw - margin * 2) / w, (ph - margin * 2) / h);
        const dw = w * scale;
        const dh = h * scale;
        pdf.addImage(dataUrl, "PNG", (pw - dw) / 2, (ph - dh) / 2, dw, dh);
        pdf.save(`org-chart-${date}.pdf`);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Org-chart export failed", e);
      toast.error(`Export failed: ${e?.message || e}`);
    } finally {
      setExporting(false);
    }
  };

  // Drag-to-reorg: a node was dropped on a would-be manager → confirm first.
  const onReorg = (empId, targetId) => {
    if (!empId || !targetId || empId === targetId) return;
    setReorgErr(null);
    setReorg({ empId, targetId });
  };

  const confirmReorg = async () => {
    if (!reorg || reorgBusy) return;
    setReorgBusy(true);
    setReorgErr(null);
    try {
      await axios.post(
        `${API_URL}/api/org-chart/assign-manager`,
        { employee_id: reorg.empId, primary_manager_id: reorg.targetId },
        authHeaders(),
      );
      setReorg(null);
      tree.refresh();
    } catch (e) {
      setReorgErr(
        e?.response?.data?.error || e.message || "Failed to reassign manager",
      );
    } finally {
      setReorgBusy(false);
    }
  };

  const renderTab = () => {
    if (tree.loading) {
      return <div className="chart-loading">Loading org chart…</div>;
    }
    if (tree.error) {
      return <div className="chart-error">⚠ {tree.error}</div>;
    }
    // Lookup so cards can render "↗ dotted: <name>" without a second fetch.
    const employeesById = new Map(
      (tree.allEmployees || []).map((e) => [e.id, e]),
    );
    return (
      <OrgCanvas
        key={activeTab}
        ref={canvasRef}
        mode={activeTab.toLowerCase()}
        roots={filtered.roots}
        orphans={filtered.orphans}
        highlightId={highlightId}
        pathIds={pathIds}
        onPick={handlePick}
        onZoomChange={setZoomPercent}
        employeesById={employeesById}
        canReorg={!isReadOnly}
        onReorg={onReorg}
      />
    );
  };

  const onAssignSaved = () => {
    setOpenAssign(false);
    tree.refresh();
  };
  const onMassSaved = () => {
    setOpenMass(false);
    tree.refresh();
  };

  return (
    <div className="contentWrapper" style={{ paddingBottom: 16 }}>
      <div className="organisation-chart">
        <div className="org-header">
          <div className="org-header-left">
            <div
              className="org-icon"
              aria-hidden="true"
              style={{
                width: 52,
                height: 52,
                borderRadius: 14,
                background: "#fff7ed",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 0,
              }}
            >
              <Network size={24} color="#f54b01" />
            </div>
            <div>
              <h2>Organisation Chart</h2>
              <p>
                {tree.total > 0
                  ? `${tree.total} employee${tree.total === 1 ? "" : "s"} · view and manage reporting structures`
                  : "View and manage reporting structures"}
              </p>
            </div>
          </div>

          <div className="org-header-right">
            <Tabs
              tabs={[
                {
                  key: "Vertical",
                  label: "Vertical",
                  icon: AlignVerticalJustifyCenter,
                },
                {
                  key: "Horizontal",
                  label: "Horizontal",
                  icon: AlignHorizontalJustifyCenter,
                },
                { key: "Network", label: "Network", icon: Network },
              ]}
              active={activeTab}
              onChange={setActiveTab}
            />
          </div>
        </div>

        <div className="org-top-panel">
          <div className="org-top-bar">
            <div className="org-search">
              <Search
                size={14}
                className="org-search-icon"
                aria-hidden="true"
              />
              <input
                type="text"
                placeholder="Search by name, role, or email"
                className="org-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="org-dept-wrap">
              <select
                className="org-dept-filter"
                value={dept}
                onChange={(e) => setDept(e.target.value)}
              >
                <option value="">All departments</option>
                {tree.departments.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
              <ChevronDown
                size={14}
                className="org-dept-caret"
                aria-hidden="true"
              />
            </div>
            <div className="org-export" style={{ position: "relative" }}>
              <button
                type="button"
                className="secondary"
                disabled={exporting || tree.loading}
                onClick={() => setExportOpen((v) => !v)}
              >
                <Download size={14} aria-hidden="true" />
                <span>{exporting ? "Exporting…" : "Export"}</span>
                <ChevronDown size={13} aria-hidden="true" />
              </button>
              {exportOpen && (
                <>
                  <div
                    onClick={() => setExportOpen(false)}
                    style={{
                      position: "fixed",
                      inset: 0,
                      zIndex: 40,
                    }}
                  />
                  <div
                    role="menu"
                    style={{
                      position: "absolute",
                      top: "calc(100% + 6px)",
                      right: 0,
                      zIndex: 41,
                      minWidth: 160,
                      background: "var(--em-bg-card, #fff)",
                      border: "1px solid var(--em-border, #e5e7eb)",
                      borderRadius: 10,
                      boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                      padding: 6,
                    }}
                  >
                    <button
                      type="button"
                      className="org-export-item"
                      onClick={() => handleExport("png")}
                      style={exportItemStyle}
                    >
                      🖼️ Download PNG
                    </button>
                    <button
                      type="button"
                      className="org-export-item"
                      onClick={() => handleExport("pdf")}
                      style={exportItemStyle}
                    >
                      📄 Download PDF
                    </button>
                  </div>
                </>
              )}
            </div>
            {!isReadOnly && (
              <div className="org-actions">
                <button onClick={() => setOpenAssign(true)}>
                  <UserPlus size={14} aria-hidden="true" />
                  <span>Assign Manager</span>
                </button>
                <button className="secondary" onClick={() => setOpenMass(true)}>
                  <Shuffle size={14} aria-hidden="true" />
                  <span>Mass Transfer</span>
                </button>
              </div>
            )}
          </div>

          <div className="org-legend">
            <div className="legend-group">
              <span className="legend-title">Legend:</span>
              <div className="legend-pill admin">
                <span className="legend-dot" />
                Root / CEO
              </div>
              <div className="legend-pill manager">
                <span className="legend-dot" />
                Manager
              </div>
              <div className="legend-pill employee">
                <span className="legend-dot" />
                Employee
              </div>
              <div className="legend-pill secondary">
                <span className="legend-dot" />
                Dotted (secondary)
              </div>
            </div>

            <div className="zoom-wrapper">
              <div className="zoom-hint">💡 Scroll to zoom</div>
              <div className="zoom-controls">
                <button
                  className="zoom-btn"
                  onClick={() => getActiveRef()?.current?.zoomOut?.()}
                >
                  −
                </button>
                <button
                  className="zoom-center"
                  onClick={() => getActiveRef()?.current?.resetZoom?.()}
                >
                  {zoomPercent}%
                </button>
                <button
                  className="zoom-btn"
                  onClick={() => getActiveRef()?.current?.zoomIn?.()}
                >
                  +
                </button>
              </div>
            </div>
          </div>
        </div>

        <div
          ref={chartAreaRef}
          className="chart-area"
          style={{
            position: "relative",
            height: chartHeight ? `${chartHeight}px` : undefined,
          }}
        >
          {renderTab()}
          {/* Refresh overlay — only shown after a save when the underlying
            tree is being reloaded (not on the initial load — that has its
            own loading state). */}
          {tree.loading && tree.total > 0 && (
            <div className="org-refresh-overlay" aria-live="polite">
              <span className="org-spinner" aria-hidden="true" />
              <span>Updating chart…</span>
            </div>
          )}
        </div>

        {!isReadOnly && (
          <>
            <AssignManagerModal
              open={openAssign}
              onClose={() => setOpenAssign(false)}
              onSaved={onAssignSaved}
              employees={tree.allEmployees}
            />
            <MassTransferModal
              open={openMass}
              onClose={() => setOpenMass(false)}
              onSaved={onMassSaved}
              employees={tree.allEmployees}
            />
          </>
        )}

        {reorg && (
          <div
            role="dialog"
            aria-modal="true"
            onClick={() => !reorgBusy && setReorg(null)}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 60,
              background: "rgba(17,24,39,0.45)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 16,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: "100%",
                maxWidth: 420,
                background: "var(--em-bg-card, #fff)",
                borderRadius: 16,
                boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
                padding: 22,
                color: "var(--em-fg1, #111827)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 12,
                }}
              >
                <h3 style={{ margin: 0, fontSize: 17 }}>Reassign manager?</h3>
                <button
                  type="button"
                  onClick={() => !reorgBusy && setReorg(null)}
                  style={{
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    color: "var(--em-fg2, #6b7280)",
                  }}
                  aria-label="Cancel"
                >
                  <X size={18} />
                </button>
              </div>
              <p style={{ margin: "0 0 16px", lineHeight: 1.5 }}>
                Set{" "}
                <strong>{nodeById.get(reorg.empId)?.name || "this employee"}</strong>
                's primary manager to{" "}
                <strong>{nodeById.get(reorg.targetId)?.name || "the target"}</strong>
                ? This changes the reporting line for them and everyone beneath
                them.
              </p>
              {reorgErr && (
                <div
                  style={{
                    background: "#fef2f2",
                    color: "#b91c1c",
                    border: "1px solid #fecaca",
                    borderRadius: 8,
                    padding: "8px 10px",
                    fontSize: 13,
                    marginBottom: 12,
                  }}
                >
                  ⚠ {reorgErr}
                </div>
              )}
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 10,
                }}
              >
                <button
                  type="button"
                  className="secondary"
                  onClick={() => setReorg(null)}
                  disabled={reorgBusy}
                >
                  Cancel
                </button>
                <button type="button" onClick={confirmReorg} disabled={reorgBusy}>
                  {reorgBusy ? "Saving…" : "Confirm reassign"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
